import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "When will I receive my order / shipment(s)?",
      answer:
        "Your shipment will be delivered within the 'Estimated delivery date' as committed at the time of order placement. You can check this by selecting your order from the 'Orders' section. Keep a lookout for our order delivery updates.\n\nNote: An order can have multiple shipments with different 'Estimated delivery date and time'.",
    },
    {
      question:
        "Order / shipment status showing delivered but I have not received it",
      answer:
        "If your order status shows delivered but you haven't received it, please contact our customer service team immediately. We will investigate and resolve the issue as quickly as possible.",
    },
    {
      question: "Which cities do you operate in?",
      answer:
        "We operate in multiple cities across Hyderabad including Panjagutta, Manikonda, Bandlaguda, Chikkadapally, and Inderbagh. Please check our Branch Locations page for a complete list of stores.",
    },
    {
      question: "How can I get my order / shipment(s) delivered faster?",
      answer:
        "You can select expedited shipping at the time of checkout to get your order delivered faster. Availability of express delivery may vary based on your location and the items ordered.",
    },
    {
      question: "Can I modify my address after Order placement?",
      answer:
        "Yes, you can modify your delivery address if your order hasn't been shipped yet. Please contact our customer service team as soon as possible to assist you with the address change.",
    },
    {
      question:
        "Are there any delivery charges for Medicine & Healthcare Orders?",
      answer:
        "Delivery is free on all orders above ₹499. For orders below this amount, a nominal delivery fee may apply depending on your location. The exact charges will be displayed at the time of checkout.",
    },
    {
      question: "Do I need a prescription to order medicines?",
      answer:
        "Yes, prescription medicines require a valid doctor's prescription. You can upload your prescription through our 'Upload Prescription' feature. Our pharmacists will verify it before processing your order.",
    },
    {
      question: "How do I track my order?",
      answer:
        "You can track your order by visiting the 'My Orders' section in your account. Real-time updates will be provided as your order moves through processing, packing, and delivery stages.",
    },
  ];

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins mt-36 lg:mt-0">
      <Helmet>
        <title>FAQs | Online Medical Store | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="MediSYS Pharmacy Online Medical Store - Buy medicines online and experience fastest home delivery."
        />
        <meta
          name="keywords"
          content="pharmacy, medical stores, online pharmacy, online pharmacy India, online medical store hyderabad"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="FAQs | MediSYS Pharmacy" />
        <meta
          property="og:description"
          content="Frequently asked questions about MediSYS Pharmacy."
        />
      </Helmet>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* ── Page header ── */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#ff601e]">
              Help Center
            </span>
          </div>
          <h1 className="font-bold text-[28px] sm:text-[34px] text-[#1e4a46] leading-tight">
            Frequently Asked Questions
          </h1>
          <p className="mt-2 text-[13px] text-[#607d7b] max-w-[440px]">
            Can't find what you're looking for? Reach out to our{" "}
            <Link
              to="/contact-us"
              className="text-[#1e4a46] font-semibold hover:underline"
            >
              support team
            </Link>
            .
          </p>
        </div>

        {/* ── FAQ accordion ── */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => {
            const isOpen = activeIndex === index;
            return (
              <div
                key={index}
                className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? "border-[#5fbdb3] shadow-[0_4px_20px_rgba(22,44,42,0.1)]"
                    : "border-[#e2eaea] shadow-[0_2px_8px_rgba(22,44,42,0.05)] hover:border-[#c5d5d4] hover:shadow-[0_4px_16px_rgba(22,44,42,0.08)]"
                }`}
              >
                {/* Question row */}
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 sm:py-5 text-left"
                  onClick={() => toggleAnswer(index)}
                >
                  {/* Index + question */}
                  <div className="flex items-start gap-3 min-w-0">
                    <span
                      className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold mt-0.5 transition-colors duration-200 ${
                        isOpen
                          ? "bg-[#1e4a46] text-white"
                          : "bg-[#eaf4f3] text-[#1e4a46]"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3
                      className={`text-[14px] sm:text-[15px] font-semibold leading-snug transition-colors duration-200 ${
                        isOpen ? "text-[#1e4a46]" : "text-[#374747]"
                      }`}
                    >
                      {faq.question}
                    </h3>
                  </div>

                  {/* Chevron */}
                  <span
                    className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${
                      isOpen
                        ? "bg-[#1e4a46] text-white rotate-180"
                        : "bg-[#f1f5f5] text-[#8faba9]"
                    }`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </button>

                {/* Answer */}
                {isOpen && (
                  <div className="px-5 pb-5">
                    <div className="border-t border-[#f1f5f5] pt-4 pl-10">
                      <div className="flex gap-3">
                        <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full" />
                        <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify whitespace-pre-line">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Still have questions CTA ── */}
        <div className="mt-10 bg-[#162c2a] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-5 relative overflow-hidden">
          {/* Plus pattern */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="relative text-center sm:text-left">
            <p className="font-bold text-[17px] sm:text-[20px] text-white leading-snug">
              Still have questions?
            </p>
            <p className="mt-1 text-[12px] sm:text-[13px] text-white/50">
              Our support team is ready to help you 24/7.
            </p>
          </div>
          <a
            href="/contact-us"
            className="relative flex-shrink-0 flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[13px] sm:text-[14px] px-6 h-[42px] rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(255,96,30,0.4)] hover:shadow-[0_5px_18px_rgba(255,96,30,0.55)] hover:-translate-y-0.5 whitespace-nowrap"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12 19.79 19.79 0 0 1 1.65 3.4 2 2 0 0 1 3.62 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
